<template>
  <DeepDialog
    v-model="dialog"
    :closable-disabled="confirmLoading"
    :persistent="confirmLoading"
    :style="{ zIndex: options.zIndex }"
    :title="title"
    width="600"
    @keydown.enter="onEnterKey"
    @keydown.esc="cancel"
  >
    <template #content>
      <span
        v-show="!!message"
        ref="contentRef"
        class="text-black text-body-large"
      >
        <!-- eslint-disable-next-line vue/no-v-html -- sanitized with DOMPurify -->
        <div v-html="sanitizedMessage"></div>
      </span>
    </template>
    <template #actions>
      <VBtnSecondary
        v-if="!options.noCancel"
        data-test-id="dialog-cancel-button"
        :disabled="confirmLoading"
        :text="i18n.t('buttons.cancel')"
        variant="outlined"
        @click="cancel"
      />

      <VBtnPrimary
        data-test-id="dialog-continue-button"
        :loading="confirmLoading"
        :text="
          options.confirmActionText
            ? options.confirmActionText
            : i18n.t('buttons.proceed')
        "
        @click="agree"
      />
    </template>
  </DeepDialog>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DOMPurify from 'dompurify';
import { DeepDialog } from '@deepcloud/deep-ui-lib';

interface ConfirmationDialogOptions {
  width?: number;
  confirmActionText?: string | null;
  noCancel?: boolean;
  zIndex?: number;
}

const i18n = useI18n();
const dialog = ref(false);
const contentRef = ref<HTMLElement | null>(null);
const confirmLoading = ref(false);
const resolve = ref<(value: boolean) => void>();
const reject = ref<(reason?: unknown) => void>();
const message = ref<string>();
const title = ref<string>();
const action = ref<(() => unknown) | null>(null);
const options = ref<ConfirmationDialogOptions>({
  width: 500,
  confirmActionText: null,
  noCancel: false,
  zIndex: 2401
});

// Messages may contain markup (e.g. links to terms documents), so they are
// rendered as HTML — but sanitized first, since some callers interpolate
// user-controlled values (file names) into them.
const sanitizedMessage = computed(() => {
  if (!message.value) return '';
  return DOMPurify.sanitize(message.value, {
    ALLOWED_TAGS: [
      'a',
      'b',
      'br',
      'code',
      'em',
      'i',
      'li',
      'ol',
      'strong',
      'ul'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class']
  });
});

function open(
  titleParam: string,
  messageParam: string,
  actionParam: (() => unknown) | null = null,
  optionsParam?: ConfirmationDialogOptions
) {
  dialog.value = true;
  title.value = titleParam;
  message.value = messageParam;
  action.value = actionParam;
  options.value = Object.assign(options.value, optionsParam);
  return new Promise<boolean>((resolveParam, rejectParam) => {
    resolve.value = resolveParam;
    reject.value = rejectParam;
  });
}

// When an action was provided, it runs while the dialog stays open with the
// confirm button in a loading state; the dialog only closes once it settles.
async function agree() {
  if (confirmLoading.value) return;
  if (action.value) {
    confirmLoading.value = true;
    try {
      await action.value();
    } finally {
      confirmLoading.value = false;
    }
  }
  resolve.value?.(true);
  dialog.value = false;
}

function cancel() {
  if (confirmLoading.value) return;
  resolve.value?.(false);
  dialog.value = false;
}

// Confirm on Enter, unless a button inside the dialog is focused (e.g. the
// user tabbed to Cancel) — there the native Enter-activates-button wins.
function onEnterKey(event: KeyboardEvent) {
  if ((event.target as HTMLElement | null)?.closest('button')) return;
  event.preventDefault();
  agree();
}

watch(
  () => dialog.value,
  (newValue, oldValue) => {
    if (!newValue && oldValue) {
      resolve.value?.(false);
    }
  }
);

// The Enter/Esc bindings above sit on the overlay root and only fire while
// keyboard focus is inside the dialog. Since Vuetify 4.1, closing another
// overlay (e.g. the file-card menu this dialog is often opened from) returns
// focus to that overlay's activator — outside this dialog — so focus the
// overlay content ourselves on open instead of relying on Vuetify to do it.
// Runs after Vuetify's focus juggling (nextTick) and is respected by it:
// both the menu's focus-restore and the dialog's own afterEnter focus leave
// an element already focused inside the dialog alone.
watch(
  () => dialog.value,
  async (isOpen) => {
    if (!isOpen) return;
    await nextTick();
    contentRef.value?.closest<HTMLElement>('.v-overlay__content')?.focus();
  }
);

defineExpose({
  agree,
  cancel,
  open
});
</script>
